'use client';

import { liteClient as algoliasearch } from 'algoliasearch/lite'; // Fixed import
import { SearchBox, InfiniteHits } from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import { algoliaAppId, algoliaSearchApiKey } from '@/lib/env';
import ProductCard from './ProductCard';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { i18n } from '@/i18n/config';
import { sendGAEvent } from '@next/third-parties/google';
import { SearchTranslations } from '@/sanity/schemaTypes/objects/translations/searchTranslationsType';
import Clarity from '@microsoft/clarity';

const searchClient = algoliasearch(algoliaAppId, algoliaSearchApiKey);

export default function Search({
  locale,
  translations
}: {
  locale: string;
  translations: SearchTranslations;
}) {
  const indexName = i18n.locales.find((l) => l.id === locale)?.algoliaIndexName;
  return (
    <InstantSearchNext
      indexName={indexName}
      searchClient={searchClient}
      routing={true}
      onStateChange={({ uiState, setUiState }) => {
        if (process.env.VERCEL_ENV === 'production' && indexName) {
          try {
            sendGAEvent('event', 'search', {
              search_term: uiState[indexName]?.query
            });
            Clarity.event('search');
          } catch (error) {
            console.warn(error);
          }
        }
        setUiState(uiState);
      }}
    >
      <SearchBox
        placeholder={translations.searchPlaceholder}
        submitIconComponent={({ classNames }) => (
          <div className={classNames.submitIcon}>
            <MagnifyingGlassIcon aria-hidden="true" className="size-5" />
            <div className="sr-only">{translations.search}</div>
          </div>
        )}
        resetIconComponent={({ classNames }) => (
          <div className={classNames.resetIcon}>
            <XMarkIcon aria-hidden="true" className="size-5" />
            <div className="sr-only">{translations.reset}</div>
          </div>
        )}
        autoFocus={true}
        classNames={{
          root: 'border px-3 md:text-xl rounded-full border-secondary/50 w-full shadow',
          form: 'flex items-stretch',
          input: 'grow outline-none p-3 appearance-none',
          submitIcon:
            'cursor-pointer grid place-content-center border-l border-gray-300 p-4 text-gray-500 hidden md:block',
          resetIcon:
            'cursor-pointer grid place-content-center border-l border-gray-300 text-gray-500 hidden md:block p-4'
        }}
      />
      <InfiniteHits
        hitComponent={({ hit }) => {
          return <ProductCard product={hit as any} locale={locale} />;
        }}
        classNames={{
          root: 'mt-10',
          list: 'my-6 grid grid-cols-1 gap-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-8',
          loadPrevious:
            'bg-secondary hover:text-secondary hover:bg-primary focus:ring-primary flex cursor-pointer mx-auto mt-12 items-center duration-75 justify-center rounded-md border border-transparent px-8 py-3 text-base font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
          loadMore:
            'bg-secondary hover:text-secondary hover:bg-primary focus:ring-primary flex cursor-pointer mx-auto mt-12 items-center duration-75 justify-center rounded-md border border-transparent px-8 py-3 text-base font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
          disabledLoadPrevious: 'hidden',
          disabledLoadMore: 'hidden'
        }}
        translations={{
          showPreviousButtonText: translations.loadPrevious,
          showMoreButtonText: translations.loadMore
        }}
      />
    </InstantSearchNext>
  );
}
